import { useState } from "react";
import { History, Loader2, RotateCcw } from "lucide-react";
import { Card } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { RichTextEditor } from "../RichTextEditor";
import { useArticleRevision, useArticleRevisions, useRestoreArticleRevision } from "../../hooks/useArticles";
import { useDateFormat } from "../../hooks/useDateFormat";
import { getErrorMessage } from "../../lib/apiErrors";
import {
  ArticleRevisionKindLabels,
  ArticleStatus,
  type ArticleDto,
  type ArticleRevisionKind,
} from "../../types/articles";

interface ArticleRevisionHistoryProps {
  article: ArticleDto;
  // While the article is being edited, restoring would pull the text out from under the editor
  disabled?: boolean;
}

/**
 * The article's saved versions, newest first (the newest 50 are kept). Opening one shows its text; restoring it gives
 * a draft article that text at once, and a published one gets it as its unpublished changes - restoring never changes
 * what readers see by itself.
 */
export function ArticleRevisionHistory({ article, disabled }: ArticleRevisionHistoryProps) {
  const { formatDateTime } = useDateFormat();
  const { data: revisions, isLoading, error } = useArticleRevisions(article.id);
  const [openRevisionId, setOpenRevisionId] = useState<string | null>(null);

  return (
    <Card className="p-3 sm:p-4 shadow-sm border border-gray-200 dark:border-gray-700">
      <h3 className="text-sm font-medium text-muted-foreground flex items-center gap-2 mb-3">
        <History className="h-4 w-4" aria-hidden="true" />
        Revision History
        {revisions && <span className="text-xs font-normal">({revisions.length})</span>}
      </h3>
      {isLoading ? (
        <div className="flex justify-center py-3">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" aria-label="Loading revision history" />
        </div>
      ) : error ? (
        <p className="text-xs text-destructive">{getErrorMessage(error, "Couldn't load the revision history")}</p>
      ) : !revisions?.length ? (
        <p className="text-xs text-muted-foreground">No saved versions yet.</p>
      ) : (
        <ul className="space-y-1 max-h-64 overflow-y-auto">
          {revisions.map((revision, index) => (
            <li key={revision.id}>
              <button
                type="button"
                onClick={() => setOpenRevisionId(revision.id)}
                className="w-full text-left p-2 rounded-md hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground">Version {revision.revisionNumber}</span>
                  <Badge variant="outline" className="text-[10px] px-1 py-0">
                    {ArticleRevisionKindLabels[revision.kind as ArticleRevisionKind] ?? "Saved"}
                  </Badge>
                  {index === 0 && <span className="text-[10px] text-muted-foreground">latest</span>}
                </div>
                <p className="text-xs text-muted-foreground">
                  {formatDateTime(revision.createdAt)}
                  {revision.createdByName ? ` · ${revision.createdByName}` : ""}
                </p>
                {revision.note && <p className="text-xs text-muted-foreground italic truncate">{revision.note}</p>}
              </button>
            </li>
          ))}
        </ul>
      )}

      {openRevisionId && (
        <RevisionDialog
          article={article}
          revisionId={openRevisionId}
          isLatest={revisions?.[0]?.id === openRevisionId}
          disabled={disabled}
          onClose={() => setOpenRevisionId(null)}
        />
      )}
    </Card>
  );
}

interface RevisionDialogProps {
  article: ArticleDto;
  revisionId: string;
  isLatest: boolean;
  disabled?: boolean;
  onClose: () => void;
}

function RevisionDialog({ article, revisionId, isLatest, disabled, onClose }: RevisionDialogProps) {
  const { formatDateTime } = useDateFormat();
  const { data: revision, isLoading, error } = useArticleRevision(article.id, revisionId);
  const restoreRevision = useRestoreArticleRevision();

  const isPublished = article.status === ArticleStatus.Published;
  const isArchived = article.status === ArticleStatus.Archived;
  const restoreHint = isArchived
    ? "Restore the article from the archive before restoring an earlier version."
    : isPublished
      ? article.hasDraft
        ? "Restoring replaces the article's current unpublished changes with this version. Readers keep seeing the published article until you publish it."
        : "Restoring puts this version in the article's unpublished changes, to review and publish. Readers keep seeing the published article until then."
      : "Restoring replaces the article's text with this version. Its current text stays in the history.";

  const handleRestore = async () => {
    try {
      await restoreRevision.mutateAsync({ articleId: article.id, revisionId });
      onClose();
    } catch {
      // Shown by the mutation hook
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {revision ? `Version ${revision.revisionNumber}` : "Version"}
            {revision && (
              <Badge variant="outline" className="ml-2 align-middle text-xs">
                {ArticleRevisionKindLabels[revision.kind as ArticleRevisionKind] ?? "Saved"}
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {revision
              ? `${formatDateTime(revision.createdAt)}${revision.createdByName ? ` by ${revision.createdByName}` : ""}${revision.note ? ` - ${revision.note}` : ""}`
              : "Loading..."}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading version" />
          </div>
        ) : error || !revision ? (
          <p className="text-sm text-destructive">{getErrorMessage(error, "Couldn't load this version")}</p>
        ) : (
          <article className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">{revision.title}</h2>
            {revision.summary && (
              <div className="p-3 rounded-lg border bg-muted/40">
                <p className="text-sm font-medium text-foreground">{revision.summary}</p>
              </div>
            )}
            {revision.tags && <p className="text-xs text-muted-foreground">Tags: {revision.tags}</p>}
            <div className="prose prose-sm max-w-none dark:prose-invert">
              <RichTextEditor content={revision.content} onChange={() => {}} editable={false} className="border-none p-0" />
            </div>
          </article>
        )}

        {!isLatest && (
          <DialogFooter className="flex-col sm:flex-row sm:items-center gap-2">
            <p className="text-xs text-muted-foreground sm:mr-auto sm:max-w-md">
              {disabled ? "Finish or cancel your edit before restoring an earlier version." : restoreHint}
            </p>
            <Button
              onClick={handleRestore}
              disabled={disabled || isArchived || !revision || restoreRevision.isPending}
            >
              <RotateCcw className="h-4 w-4 mr-2" aria-hidden="true" />
              {restoreRevision.isPending ? "Restoring..." : "Restore this version"}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
